import { downloadFile } from '../components/ImportExportCenter';
import { getUserTrialStatus } from './userManagement';
import { auth } from '../lib/firebase';

export interface CurrencyTier {
  code: string;
  symbol: string;
  label: string;
  flag: string;
  prices: {
    starter: { monthly: number; yearly: number };
    pro: { monthly: number; yearly: number };
    enterprise: { monthly: number; yearly: number };
  };
}

export const SUPPORTED_CURRENCIES: Record<string, CurrencyTier> = {
  ZAR: {
    code: 'ZAR',
    symbol: 'R',
    label: 'South African Rand',
    flag: '🇿🇦',
    prices: {
      starter: { monthly: 0, yearly: 0 },
      pro: { monthly: 499, yearly: 4990 },
      enterprise: { monthly: 1699, yearly: 16990 }
    }
  },
  USD: {
    code: 'USD',
    symbol: '$',
    label: 'US Dollar',
    flag: '🇺🇸',
    prices: {
      starter: { monthly: 0, yearly: 0 },
      pro: { monthly: 29, yearly: 290 },
      enterprise: { monthly: 99, yearly: 990 }
    }
  },
  EUR: {
    code: 'EUR',
    symbol: '€',
    label: 'Euro',
    flag: '🇪🇺',
    prices: {
      starter: { monthly: 0, yearly: 0 },
      pro: { monthly: 27, yearly: 270 },
      enterprise: { monthly: 89, yearly: 890 }
    }
  },
  GBP: {
    code: 'GBP',
    symbol: '£',
    label: 'British Pound',
    flag: '🇬🇧',
    prices: {
      starter: { monthly: 0, yearly: 0 },
      pro: { monthly: 24, yearly: 240 },
      enterprise: { monthly: 79, yearly: 790 }
    }
  },
  CAD: {
    code: 'CAD',
    symbol: 'C$',
    label: 'Canadian Dollar',
    flag: '🇨🇦',
    prices: {
      starter: { monthly: 0, yearly: 0 },
      pro: { monthly: 39, yearly: 390 },
      enterprise: { monthly: 135, yearly: 1350 }
    }
  },
  AUD: {
    code: 'AUD',
    symbol: 'A$',
    label: 'Australian Dollar',
    flag: '🇦🇺',
    prices: {
      starter: { monthly: 0, yearly: 0 },
      pro: { monthly: 45, yearly: 450 },
      enterprise: { monthly: 149, yearly: 1490 }
    }
  }
};

export function detectUserCurrency(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    if (tz.includes('Johannesburg') || tz.includes('Africa/')) {
      return 'ZAR';
    }
    if (tz.includes('London') || tz.includes('Europe/Belfast')) {
      return 'GBP';
    }
    if (tz.includes('Europe/')) {
      return 'EUR';
    }
    if (tz.includes('Australia/') || tz.includes('Sydney') || tz.includes('Melbourne')) {
      return 'AUD';
    }
    if (tz.includes('Toronto') || tz.includes('Vancouver') || tz.includes('Canada/')) {
      return 'CAD';
    }
  } catch (e) {
    console.warn('Could not auto-detect timezone currency:', e);
  }
  return 'ZAR'; // Default to ZAR for founder's primary market or fallback
}

export function getPlanPriceForCurrency(
  planId: string, 
  cycle: 'monthly' | 'yearly', 
  currencyCode: string
): { amount: number; formatted: string; symbol: string } {
  const curr = SUPPORTED_CURRENCIES[currencyCode] || SUPPORTED_CURRENCIES.ZAR;
  const planPrices = curr.prices[planId as keyof typeof curr.prices] || curr.prices.pro;
  const amount = cycle === 'yearly' ? planPrices.yearly : planPrices.monthly;
  
  const formatted = `${curr.symbol}${amount.toLocaleString()}`;
  return { amount, formatted, symbol: curr.symbol };
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  tagline: string;
  monthlyPrice: number;
  yearlyPrice: number;
  popular?: boolean;
  features: string[];
  maxInvoices: number; // -1 for unlimited
  maxAiCredits: number;
  maxTeamMembers: number;
  vaultStorage: string;
}

export interface PaymentMethod {
  id: string;
  brand: 'visa' | 'mastercard' | 'amex' | 'paypal';
  last4: string;
  expMonth: string;
  expYear: string;
  holderName: string;
  isDefault: boolean;
  country: string;
  zipCode: string;
}

export interface BillingHistoryItem {
  id: string;
  invoiceNumber: string;
  date: string;
  description: string;
  amount: number;
  currency: string;
  status: 'paid' | 'refunded' | 'pending';
  paymentMethodLast4: string;
  downloadUrl?: string;
}

export interface SubscriptionData {
  currentPlanId: string;
  billingCycle: 'monthly' | 'yearly';
  status: 'active' | 'trialing' | 'canceled' | 'past_due';
  startDate: string;
  nextBillingDate: string;
  autoRenew: boolean;
  billingEmail: string;
  billingName?: string;
  billingTaxId?: string;
  billingAddress?: string;
  billingCountry?: string;
  paymentMethods: PaymentMethod[];
  history: BillingHistoryItem[];
}

export const DODO_PRODUCT_IDS = {
  pro_monthly: 'pdt_0NlSnTFvMVVdV72YRJvPG',
  pro_yearly: 'pdt_0NlSnV3xxCI2SFx0g34Vt',
  pro_annual: 'pdt_0NlSnV3xxCI2SFx0g34Vt',
} as const;

export const AVAILABLE_PLANS: SubscriptionPlan[] = [
  {
    id: 'pro',
    name: 'Sovereign Founder Pro',
    tagline: 'Complete financial automation, tax compliance & AI strategic intelligence',
    monthlyPrice: 29,
    yearlyPrice: 290,
    popular: true,
    features: [
      'Unlimited Client Invoices, Bookings & Expenses',
      'Multi-Currency & Automatic VAT Tax Engine',
      'AI Co-Founder Strategic Engine (Strictly BYOK Direct)',
      'Up to 5 Whitelisted Founder Seats',
      'AES-256 Cloud Crypto Vault & Realtime Sync',
      'Automated Audit Logging & PDF Export'
    ],
    maxInvoices: -1,
    maxAiCredits: 99999,
    maxTeamMembers: 5,
    vaultStorage: '10 GB'
  },
  {
    id: 'enterprise',
    name: 'AzuraOS Enterprise Suite',
    tagline: 'Dedicated cloud infrastructure, custom tax filing & 24/7 strategic support',
    monthlyPrice: 99,
    yearlyPrice: 990,
    features: [
      'Everything in Sovereign Founder Pro',
      'Unlimited AI Co-Founder Strategy Engine (Strictly BYOK)',
      'Unlimited Whitelisted User Seats',
      'Custom Multi-Entity Tax Department Rules',
      'Dedicated Cloud SQL & Firestore Replication',
      '24/7 Sovereign Founder VIP Support'
    ],
    maxInvoices: -1,
    maxAiCredits: 99999,
    maxTeamMembers: 99,
    vaultStorage: 'Unlimited'
  }
];

export interface SubscriptionCheckResult {
  allowed: boolean;
  currentCount: number;
  maxAllowed: number;
  planId: string;
  planName: string;
  message?: string;
}

/**
 * Service layer function to check if user has reached their subscription tier limits.
 * - Active 7-Day Free Trial & Sovereign Founder Pro / Enterprise: 100% Unlimited entries & features
 * - Expired Trial without Pro: Hard paywall required to resume command
 * - AI Co-Founder is strictly BYOK (Bring Your Own Key) with zero middleman markups
 */
export function checkSubscriptionLimits(
  type: 'invoice' | 'expense' | 'booking' | 'ai_query',
  currentCount: number,
  currentUserEmail?: string | null
): SubscriptionCheckResult {
  const subData = getSubscriptionData();
  const planId = subData.currentPlanId || 'pro';
  const plan = AVAILABLE_PLANS.find(p => p.id === planId) || AVAILABLE_PLANS[0];

  // Users in active 7-day trial or on paid plans (pro/enterprise) have full unlocked capabilities
  const trial = getUserTrialStatus(currentUserEmail);
  const isTrialActive = trial.isTrialActive && !trial.isTrialExpired;
  const isSubscribed = trial.isSubscribed || trial.isOwner || (subData.status === 'active' && !!subData.billingEmail) || planId === 'enterprise';
  const isProOrTrial = isTrialActive || isSubscribed || subData.status === 'trialing';

  if (!isProOrTrial) {
    return {
      allowed: false,
      currentCount,
      maxAllowed: 0,
      planId: 'expired_trial',
      planName: 'Evaluation Completed',
      message: 'Your 7-day full access evaluation has ended. Unlock Sovereign Founder Pro to resume full command.'
    };
  }

  if (type === 'ai_query') {
    // Check if user has connected a BYOK key in localStorage
    const byokKey = typeof window !== 'undefined' ? localStorage.getItem('azura_os_gemini_key') : null;
    const hasValidKey = !!(byokKey && byokKey.trim().length >= 10);

    const maxAllowed = hasValidKey ? 99999 : 500;
    const allowed = hasValidKey || currentCount < maxAllowed;

    return {
      allowed,
      currentCount,
      maxAllowed,
      planId: plan.id,
      planName: isTrialActive ? 'Sovereign Founder Pro (7-Day Evaluation)' : plan.name,
      message: allowed
        ? undefined
        : `Daily AI Query Limit Reached (${currentCount}/${maxAllowed}). Connect your personal Google AI Studio key (BYOK) for unmetered queries.`
    };
  } else {
    // For bookings, expenses, invoices
    return {
      allowed: true,
      currentCount,
      maxAllowed: Infinity,
      planId: plan.id,
      planName: isTrialActive ? 'Sovereign Founder Pro (7-Day Evaluation)' : plan.name
    };
  }
}

export const DEFAULT_SUBSCRIPTION_DATA: SubscriptionData = {
  currentPlanId: 'pro',
  billingCycle: 'monthly',
  status: 'trialing',
  startDate: new Date().toISOString().split('T')[0],
  nextBillingDate: new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0],
  autoRenew: true,
  billingEmail: '',
  paymentMethods: [],
  history: []
};

const STORAGE_KEY = 'azuraos_subscription_data';

export function getSubscriptionData(userEmail?: string | null): SubscriptionData {
  try {
    const cleanEmail = (
      userEmail || 
      auth.currentUser?.email || 
      (typeof window !== 'undefined' ? localStorage.getItem('solostack_local_user_email') : '') || 
      ''
    ).trim().toLowerCase();

    if (!cleanEmail) {
      return { ...DEFAULT_SUBSCRIPTION_DATA, billingEmail: '', history: [] };
    }

    const scopedKey = `azuraos_sub_data_${cleanEmail}`;
    let saved = localStorage.getItem(scopedKey);

    if (saved) {
      const parsed = JSON.parse(saved);
      // Validate that the cached subscription actually belongs to this user
      if (parsed.billingEmail && parsed.billingEmail.toLowerCase() !== cleanEmail) {
        // Mismatch! Do not leak another user's subscription or receipt
        return { ...DEFAULT_SUBSCRIPTION_DATA, billingEmail: cleanEmail, history: [] };
      }
      if (parsed.billingName && (parsed.billingName.includes('Raksha') || parsed.billingName.includes('Primary Founder'))) {
        parsed.billingName = '';
      }
      if (Array.isArray(parsed.history)) {
        parsed.history = parsed.history.filter((h: BillingHistoryItem) => 
          !h.id?.startsWith('sub_inv_2026_') && h.invoiceNumber !== 'AZ-INV-2026-0811' && !h.id?.startsWith('pm_')
        );
      }
      // Always keep payment methods empty - payment cards are never stored or rendered on client
      parsed.paymentMethods = [];
      return { ...DEFAULT_SUBSCRIPTION_DATA, ...parsed, billingEmail: cleanEmail, paymentMethods: [] };
    }
  } catch (e) {
    console.error('Failed to parse subscription data from localStorage:', e);
  }
  return { ...DEFAULT_SUBSCRIPTION_DATA, billingEmail: userEmail || auth.currentUser?.email || '', history: [] };
}

export function saveSubscriptionData(data: SubscriptionData, userEmail?: string | null): void {
  try {
    const cleanEmail = (userEmail || data.billingEmail || auth.currentUser?.email || '').trim().toLowerCase();
    if (!cleanEmail) return;

    const scopedKey = `azuraos_sub_data_${cleanEmail}`;
    const sanitizedData = {
      ...data,
      billingEmail: cleanEmail,
      paymentMethods: []
    };

    localStorage.setItem(scopedKey, JSON.stringify(sanitizedData));

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('subscription_data_updated', { detail: sanitizedData }));
    }
  } catch (e) {
    console.error('Failed to save subscription data to localStorage:', e);
  }
}

export interface InvoiceBillingDetails {
  name?: string;
  email?: string;
  taxId?: string;
  address?: string;
  country?: string;
}

export function downloadSubscriptionInvoice(
  item: BillingHistoryItem, 
  billingDetails?: string | InvoiceBillingDetails
): void {
  const details: InvoiceBillingDetails = typeof billingDetails === 'string' 
    ? { name: billingDetails } 
    : (billingDetails || {});

  const recipientName = details.name?.trim() || 'Workspace Account Holder';
  const recipientEmail = details.email?.trim() ? `Email: ${details.email}` : '';
  const recipientTax = details.taxId?.trim() ? `VAT / Tax Registration: ${details.taxId}` : '';
  const recipientAddress = details.address?.trim() ? `Billing Address: ${details.address}` : '';

  const billedToLines = [
    `  ${recipientName}`,
    recipientEmail ? `  ${recipientEmail}` : '',
    recipientTax ? `  ${recipientTax}` : '',
    recipientAddress ? `  ${recipientAddress}` : '',
    `  Payment Method: Secured Electronic Checkout (Dodo Payments)`
  ].filter(Boolean).join('\n');

  const receiptContent = `====================================================
AZURAOS SUBSCRIPTION RECEIPT & TAX INVOICE
====================================================
Invoice Number: ${item.invoiceNumber}
Date Issued: ${item.date}
Payment Status: ${item.status.toUpperCase()}

Merchant of Record / Processor:
  Dodo Payments Inc. (On behalf of AzuraOS)
  Digital Services & Workflow Software

Billed To (Customer / Tax Entity):
${billedToLines}

Subscription Details:
  Item: ${item.description}
  Amount Paid: ${item.currency} ${item.amount.toFixed(2)}
  Tax / VAT: Inclusive where applicable by digital service jurisdiction

Total Paid: ${item.currency} ${item.amount.toFixed(2)}

====================================================
Tax Accounting Notice:
This document serves as an official electronic receipt and record of subscription purchase for deductible business software expenses and tax accounting purposes.
====================================================`;

  downloadFile(receiptContent, `receipt_${item.invoiceNumber.toLowerCase()}.txt`, 'text/plain');
}
